import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, MenuItem, TextField } from '@mui/material';
import DrawerMobile from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import QRFullLogo from '~/assets/icons/sidebar-logo-new.svg?react';
import { language } from '~/constants/language';
import { cookieService } from '~/services/cookie-service';
import { COLORS } from '~/styles/colors';
import { getInitialRoute, isMobileResolution } from '~/utils/common';

import { Drawer, DrawerHeader } from './styles/styled';
import { sidebarService } from './utils';

type AnonMobileSideBarProps = PropsWithChildren<{
  open: boolean;
  setOpen: (value: boolean) => void;
}>;
function AnonMobileSideBar({ open, setOpen, children }: AnonMobileSideBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const changeLanguage = (event) => {
    localStorage.setItem('language', event.target.value);
    i18n.changeLanguage(event.target.value);
  };

  const getLanguage = () => {
    return localStorage.getItem('language') || 'en';
  };

  const isAccessCookie = cookieService.getCookie('access') || null;
  const isActiveItem = (tab) => location.pathname === tab.path && open;
  const toggleSidebar = () => setOpen((open) => !open);

  const initialRoute = useMemo(
    () => getInitialRoute({ isAuthenticated: isAccessCookie, pathname: location.pathname }),
    [isAccessCookie, location.pathname],
  );

  const DrawerComponent = isMobileResolution ? DrawerMobile : Drawer;

  if (initialRoute && initialRoute !== location.pathname) {
    return <Navigate to={initialRoute} />;
  }

  return (
    <>
      {isMobileResolution ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <DrawerComponent
            variant={isMobileResolution ? 'temporary' : 'permanent'}
            open={open}
            ModalProps={{
              onBackdropClick: () => setOpen(false),
            }}
            sx={{ display: !isMobileResolution && !open && 'none', zIndex: 1001 }}
            PaperProps={{
              sx: {
                backgroundColor: open ? COLORS.white : COLORS.white,
              },
            }}
          >
            <DrawerHeader sx={{ justifyContent: 'center', padding: 0 }}>{!!open && <QRFullLogo />}</DrawerHeader>
            <List sx={{ height: '100%' }}>
              {sidebarService.getRoutesName().map((text, index) => (
                <ListItem key={index} disablePadding className={`sidebar__item ${isActiveItem(text) && 'm_active'}`}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                    onClick={() => {
                      navigate(text.path);
                      setOpen(false);
                    }}
                  >
                    {/* <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: setSidebarItemColor(open, text),
                    }}
                  >
                    {renderSidebarIcon(index)}
                  </ListItemIcon> */}

                    <ListItemText
                      primary={t(`buttons:${text.name}`)}
                      sx={{
                        opacity: open ? 1 : 0,
                        color: isActiveItem(text) ? COLORS.black : COLORS.disableGrayColor,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 3 }}>
                  <LanguageIcon sx={{ fontSize: 18, mr: 0, color: 'gray' }} />
                  <Box sx={{ display: 'flex' }}>
                    <TextField
                      select
                      variant="outlined"
                      sx={{
                        width: '105px',
                        '& .MuiInputBase-input': {
                          color: 'gray',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                          },
                        },
                        borderWidth: 0,
                      }}
                      defaultValue={getLanguage()}
                      onChange={changeLanguage}
                    >
                      {language.map((option) => (
                        <MenuItem key={option.value} sx={{ color: 'black' }} value={option.value} defaultValue={'en'}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Box>
            </List>
            {!!open && (
              <Box className="sidebar__close-container" onClick={toggleSidebar}>
                <ArrowCircleLeftIcon sx={{ color: COLORS.lightBlue07, margin: '0 10% 0 7%' }} />
                <Typography color={COLORS.lightBlue07}>{t('sidebar:hideSidebar')}</Typography>
              </Box>
            )}
          </DrawerComponent>
          {children}
        </Box>
      ) : (
        { ...children }
      )}
    </>
  );
}

export default AnonMobileSideBar;
